import { additionalRooms } from 'src/lib/constants'
import { INormalizedPlan, IPendingPlan } from 'src/types/modifiedPlan.interface'

export interface NormalizedPlanData {
  [key: string]: unknown
}

const PROPERTY_MAP: Record<string, keyof INormalizedPlan> = {
  '1st Floor Sq Ft': 'firstFloorSqft',
  '2nd Floor Sq Ft': 'secondFloorSqft',
  '3rd Floor Sq Ft': 'thirdFloorSqft',
  '4th Floor Sq Ft': 'fourthFloorSqft',
  '5th Floor Sq Ft': 'fifthFloorSqft',
  '6th Floor Sq Ft': 'sixthFloorSqft',
  '7th Floor Sq Ft': 'seventhFloorSqft',
  '8th Floor Sq Ft': 'eighthFloorSqft',
  '9th Floor Sq Ft': 'ninthFloorSqft',
  'Basement Sq Ft': 'basementSqft',
  Bedrooms: 'bedrooms',
  'Bonus Room Sq Ft': 'bonusRoomSqFt',
  Box_File_Id: 'boxFileId',
  'Community Number': 'communityNumber',
  'Deck Size - Sq Ft': 'deckSizeSqFt',
  'Depth of Plan In.': 'depthOfThePlanInInch',
  'Depth of the Plan Ft': 'depthOfThePlanInFeet',
  Division: 'division',
  Entry: 'entry',
  'Entry Size - Sq Ft': 'entrySizeSqft',
  'Exterior Cladding': 'exteriorCladding',
  'Exterior Wall Construction': 'exteriorWallConstruction',
  'Family Room': 'familyRoom',
  'Family Room Location': 'familyRoomLocation',
  'Family Room Sq Ft': 'familyRoomSqFt',
  'Flex Sq Ft': 'flexSqFt',
  'Formal Dining Room': 'formalDiningRoom',
  'Foundation Type': 'foundationType',
  'Full Bathrooms': 'fullBathrooms',
  'Game Room Sq Ft': 'gameRoomSqFt',
  'Garage Att/Det': 'garageType',
  'Garage Load': 'garageLoad',
  'Garage Size - Sq Ft': 'garageSize',
  'Golf Cart Garage Size - Sq Ft': 'golfCartGarageSize',
  'Great Room - Sq Ft': 'greatRoomSqFt',
  'Half Bathrooms': 'halfBathrooms',
  'Interior Wall Construction': 'interiorWallConstruction',
  'Lanai/Enclosures Size - Sq Ft': 'lanaiEnclosuresSizeSqFt',
  'Loft Sq Ft': 'loftSqFt',
  'Marketing Name': 'marketingName',
  'Material Roof Type': 'materialRoofType',
  'No. of Indoor Fire Places': 'numberOfIndoorFireplaces',
  'No. of Outdoor Fire Places': 'numberOfOutdoorFireplaces',
  'Number of Cars/Garages': 'numberOfCarGarages',
  'Number of Dining Areas': 'numberOfDiningAreas',
  'Optional Garage Number of Cars/Garages': 'optionalGarageNumberOfCarGarages',
  'Optional Garage Size - Sq Ft': 'optionalGarageSize',
  'Owners Suite Location': 'ownersSuiteLocation',
  Patio: 'patio',
  'Patio Size - Sq Ft': 'patioSizeSqFt',
  Plan: 'plan',
  'Plan Change Number': 'planChangeNumber',
  'Plan Elevation': 'planElevation',
  'Roof Shape': 'roofShape',
  'RV Garage Size - Sq Ft': 'rvGarageSize',
  Source_File: 'sourceFile',
  Stories: 'stories',
  'Study Sq Ft': 'studySqFt',
  'Sun Room Sq Ft': 'sunRoomSqFt',
  'Total Home Sq Ft': 'homeSqft',
  Uploaded_By: 'uploadedBy',
  'Width of Plan In.': 'widthOfThePlanInInch',
  'Width of the Plan Ft': 'widthOfThePlanInFeet',
  'Year of Creation': 'yearOfCreation',
}

export const normalizeFlatPlanData = (
  flatData: Record<string, unknown>
): INormalizedPlan => {
  const normalized: INormalizedPlan = {}

  Object.entries(flatData).forEach(([originalKey, value]) => {
    const normalizedKey =
      PROPERTY_MAP[originalKey] ||
      (convertToLowerCamelCase(originalKey) as keyof INormalizedPlan)
    normalized[normalizedKey] = value
  })

  return normalized
}

const convertToLowerCamelCase = (str: string): string => {
  return str
    .replace(/[\s_-]+(.)/g, (_, char) => char.toUpperCase())
    .replace(/^(.)/, (match) => match.toLowerCase())
}

const toSafeNumber = (value: unknown, defaultValue = 0): number => {
  if (value === null || value === undefined) {
    return defaultValue
  }
  const num = Number(value)
  return Number.isNaN(num) ? defaultValue : num
}

export const mapNormalizedPlanToModifiedPlan = (
  normalizedPlan: INormalizedPlan
): IPendingPlan => {
  const {
    architecturalFirm,
    architecturalShake,
    architecturalShingle,
    baseHomePrice,
    baseHousePre,
    basement,
    basementSqft,
    basementType,
    basePlan,
    bedrooms,
    boardAndBatten,
    boxFileId,
    communityName,
    communityNumber,
    deck,
    depthOfThePlanInFeet,
    depthOfThePlanInInch,
    division,
    effectiveDate,
    eighthFloorSqft,
    engineerName,
    engineerOfRecord,
    entry,
    entrySizeSqft,
    exteriorCladding,
    exteriorWallConstruction,
    fifthFloorSqft,
    firmName,
    firstFloorCeilingHeightInFeet,
    firstFloorSqft,
    formalDiningRoom,
    formalLivingRoom,
    foundationType,
    fourthFloorSqft,
    fullBathrooms,
    garageAlignment,
    garageLoad,
    garageSize,
    garageType,
    golfCartGarage,
    golfCartGarageAttachedDetached,
    golfCartGarageSize,
    halfBathrooms,
    hierarchy,
    homeSqft,
    id,
    impactWindows,
    indoorFireplace,
    interiorSprinklers,
    interiorWallConstruction,
    lanaiEnclosures,
    livingAreas,
    marketingName,
    materialRoofType,
    ninthFloorSqft,
    numberOfCarGarages,
    numberOfDiningAreas,
    numberOfFixtures,
    optionalGarage,
    optionalGarageNumberOfCarGarages,
    optionalGarageSize,
    optionalGarageType,
    outdoorFireplace,
    outdoorKitchen,
    ownersSuiteLocation,
    patio,
    plan,
    planCategory,
    planChangeNumber,
    planCreatedDateTime,
    planElevation,
    planModifiedDateTime,
    planOwnedByLennar,
    planStatus,
    planType,
    possibleMatches,
    region,
    roofShape,
    rvGarage,
    rvGarageAttachedDetached,
    rvGarageSize,
    secondFloorSqft,
    series,
    seventhFloorSqft,
    sidingLap,
    sidingPanel,
    sixthFloorSqft,
    sourceFile,
    sqftOfHomeConditionedSpace,
    sqftOfHomeUnderRoof,
    stories,
    stormShutters,
    structureType,
    targetProfitPerc,
    thirdFloorSqft,
    uploadedBy,
    valueEngineered,
    vaultedCeiling,
    widthOfThePlanInFeet,
    widthOfThePlanInInch,
    yearOfCreation,
  } = normalizedPlan

  const planName = planElevation ? `${plan} - ${planElevation}` : String(plan)

  return {
    _etag: '',
    additionalRooms: additionalRooms
      .filter((room) => normalizedPlan[room.key as keyof typeof normalizedPlan])
      .map((room) => room.label)
      .join(', '),
    architecturalFirm,
    architecturalShake,
    architecturalShingle,
    baseHomePrice: toSafeNumber(baseHomePrice),
    baseHousePre: toSafeNumber(baseHousePre),
    basement: basement ? 'Y' : 'N',
    basementSqft: toSafeNumber(basementSqft),
    basementType,
    basePlan: basePlan ?? '',
    bedrooms: toSafeNumber(bedrooms),
    boardAndBatten,
    boxFileId,
    changeNumber: String(planChangeNumber ?? ''),
    communityId: communityNumber ?? '',
    communityName: communityName ?? '',
    communityPlanId: id ?? '',
    deck: deck ? 'Y' : 'N',
    depthOfThePlanInFeet: toSafeNumber(depthOfThePlanInFeet),
    depthOfThePlanInInch: toSafeNumber(depthOfThePlanInInch),
    division: division ?? '',
    effectiveDate: effectiveDate ?? '',
    eighthFloorSqft: toSafeNumber(eighthFloorSqft),
    engineerName: engineerName ?? '',
    engineerOfRecord: engineerOfRecord ?? '',
    entry: entry ?? '',
    entrySqft: toSafeNumber(entrySizeSqft),
    exteriorCladding,
    exteriorWallConstruction,
    fifthFloorSqft: toSafeNumber(fifthFloorSqft),
    firmName,
    firstFloorCeilingHeightInFeet: toSafeNumber(firstFloorCeilingHeightInFeet),
    firstFloorSqft: toSafeNumber(firstFloorSqft),
    formalDiningRoom: formalDiningRoom ? 'Y' : 'N',
    formalLivingRoom: formalLivingRoom ? 'Y' : 'N',
    foundationType,
    fourthFloorSqft: toSafeNumber(fourthFloorSqft),
    fullBathrooms: toSafeNumber(fullBathrooms),
    garageAlignment,
    garageLoad,
    garageSize: toSafeNumber(garageSize),
    garageType,
    golfCartGarage,
    golfCartGarageAttachedDetached: golfCartGarageAttachedDetached ?? '',
    golfCartGarageSize: toSafeNumber(golfCartGarageSize),
    halfBathrooms: toSafeNumber(halfBathrooms),
    hidden: false,
    hierarchy,
    homeSqft: homeSqft ?? 0,
    id: id ?? '',
    impactWindows: impactWindows ? 'Y' : 'N',
    indoorFireplace: indoorFireplace ? 'Y' : 'N',
    interiorSprinklers: interiorSprinklers ? 'Y' : 'N',
    interiorWallConstruction,
    isSyncedWithJDE: false,
    lanaiEnclosures: lanaiEnclosures ? 'Y' : 'N',
    livingAreas: toSafeNumber(livingAreas),
    marketingName: marketingName ?? '',
    masterBedroomLocation: toSafeNumber(ownersSuiteLocation),
    materialRoofType,
    ninthFloorSqft: toSafeNumber(ninthFloorSqft),
    numberOfCarGarages,
    numberOfDiningAreas: toSafeNumber(numberOfDiningAreas),
    numberOfFixtures: toSafeNumber(numberOfFixtures),
    optionalGarage,
    optionalGarageNumberOfCarGarages: toSafeNumber(
      optionalGarageNumberOfCarGarages
    ),
    optionalGarageSize: toSafeNumber(optionalGarageSize),
    optionalGarageType,
    outdoorFireplace: outdoorFireplace ? 'Y' : 'N',
    outdoorKitchen: outdoorKitchen ? 'Y' : 'N',
    patio: patio ? 'Y' : 'N',
    plan: String(plan ?? ''),
    planCategory: planCategory ?? '',
    planCreatedBy: uploadedBy ?? '',
    planCreatedDateTime: planCreatedDateTime ?? '',
    planElevation: planElevation ?? '',
    planModifiedDateTime: planModifiedDateTime ?? '',
    planName,
    planOwnedByLennar: planOwnedByLennar ? 'Y' : 'N',
    planStatus: planStatus ?? '',
    planType: planType ?? '',
    possibleMatches: possibleMatches ?? [],
    region: region ?? '',
    roofShape,
    rvGarage,
    rvGarageAttachedDetached,
    rvGarageSize: toSafeNumber(rvGarageSize),
    secondFloorSqft: toSafeNumber(secondFloorSqft),
    series,
    seventhFloorSqft: toSafeNumber(seventhFloorSqft),
    sidingLap,
    sidingPanel,
    sixthFloorSqft: toSafeNumber(sixthFloorSqft),
    sourceFile,
    sqftOfHomeConditionedSpace: toSafeNumber(sqftOfHomeConditionedSpace),
    sqftOfHomeUnderRoof: toSafeNumber(sqftOfHomeUnderRoof),
    stories: toSafeNumber(stories),
    stormShutters: stormShutters ? 'Y' : 'N',
    structureType,
    submitted: false,
    supportingDocuments: [],
    targetProfitPercentage: toSafeNumber(targetProfitPerc),
    thirdFloorSqft: toSafeNumber(thirdFloorSqft),
    valueEngineered: valueEngineered ? 'Y' : 'N',
    vaultedCeiling: vaultedCeiling ? 'Y' : 'N',
    widthOfThePlanInFeet: toSafeNumber(widthOfThePlanInFeet),
    widthOfThePlanInInch: toSafeNumber(widthOfThePlanInInch),
    yearOfCreation: toSafeNumber(yearOfCreation),
  }
}

export const normalizeFlatPlanDataArray = (
  dataArray: Array<Record<string, unknown>>
): INormalizedPlan[] => {
  return dataArray?.map(normalizeFlatPlanData)
}

export const normalizeFlatPlanDataAndMapToModifiedPlan = (
  dataArray: Array<Record<string, unknown>>
): IPendingPlan[] => {
  const normalizedArray: INormalizedPlan[] =
    normalizeFlatPlanDataArray(dataArray)
  return normalizedArray?.map(mapNormalizedPlanToModifiedPlan)
}
